import { type ReactNode } from "react";
import { useTranslate } from "@refinedev/core";
import { PencilIcon, PlusIcon, StatusBadge } from "@fawkes/ui";

import type { ComponentStatus, DierComponentDef } from "./dierComponenten";

/**
 * Eén onderdeel binnen een samengestelde diertegel.
 *
 * <p>Uit het ontwerp van 30-09-2026: elk onderdeel leest hetzelfde, in vier lagen. De <b>waarde</b>
 * (wat het nu is), de <b>herkomst</b> (wanneer en door wie), de <b>status</b> (in orde, aandacht,
 * verlopen) en een <b>actie</b>. Dat patroon staat hier één keer, zodat de bediening herkenbaar blijft
 * ook als tegels er heel verschillend uitzien — een ring en een statuslijst tonen hetzelfde onderdeel
 * anders, maar je klikt op dezelfde plek hetzelfde weg.
 *
 * <p><b>De knop zegt wat hij doet.</b> Een plus voegt een registratie toe aan de historie, een potlood
 * overschrijft vaste informatie. Welke van de twee het is staat niet hier maar in de
 * componentbibliotheek, zodat hetzelfde onderdeel in elke tegelvorm dezelfde knop krijgt.
 *
 * <p>Zonder schrijfrecht blijft het onderdeel staan en verdwijnt alleen de knop: informatie weglaten
 * omdat iemand niet mag schrijven leest als een gat in de gegevens.
 */

const STATUS_TOON: Record<ComponentStatus, "success" | "warning" | "danger"> = {
  inOrde: "success",
  aandacht: "warning",
  verlopen: "danger",
};

export function DierOnderdeel({
  def,
  waarde,
  herkomst,
  status,
  magSchrijven = true,
  onActie,
  children,
}: {
  def: DierComponentDef;
  /** Wat het nu is. Leeg betekent: nog niets geregistreerd — en dat is iets anders dan nul. */
  waarde: ReactNode;
  /** Wanneer en door wie; leeg laten als dat niet bekend is. */
  herkomst?: ReactNode;
  status?: ComponentStatus;
  magSchrijven?: boolean;
  onActie?: () => void;
  /** Extra inhoud onder de waarde, bijvoorbeeld een trend of een korte instructie. */
  children?: ReactNode;
}) {
  const t = useTranslate();
  const Icoon = def.icoon;

  return (
    <div className="do-onderdeel">
      <span className="do-onderdeel__icoon" aria-hidden="true"><Icoon size={16} /></span>
      <div className="do-onderdeel__tekst">
        <span className="do-onderdeel__label">{t(def.label)}</span>
        <span className="do-onderdeel__waarde">
          {waarde ?? <span className="do-onderdeel__leeg">{t("dierTegel.nogNiets")}</span>}
        </span>
        {/* De status staat in de tekstkolom en niet ernaast. Als eigen kolom nam hij vaste breedte, en
            in een smalle tegel bleef er dan zo weinig voor de tekst over dat "Ontworming" per letter
            afbrak. Nu krimpt de tekst pas als de status er niet meer naast past. */}
        {status ? (
          <span className="do-onderdeel__status">
            <StatusBadge tone={STATUS_TOON[status]}>{t(`dierTegel.status.${status}`)}</StatusBadge>
          </span>
        ) : null}
        {herkomst ? <span className="do-onderdeel__herkomst">{herkomst}</span> : null}
        {children}
      </div>
      {magSchrijven && onActie ? (
        <button
          type="button"
          className="do-onderdeel__actie"
          data-testid={`onderdeel-actie-${def.sleutel}`}
          onClick={onActie}
          aria-label={t(`dierTegel.actie.${def.actie}`) + ": " + t(def.label)}
          title={t(`dierTegel.actie.${def.actie}`)}
        >
          {def.actie === "registratie"
            ? <PlusIcon width={15} height={15} />
            : <PencilIcon width={15} height={15} />}
        </button>
      ) : null}
    </div>
  );
}
